import { Box, Container, Link, Paper, Typography } from '@mui/material'
import { EthAccountButton } from '@xylabs/sdk-react'
import { useContext } from 'react'
import { Helmet } from 'react-helmet'
import { Link as RouterLink } from 'react-router-dom'

import { EthersContext, NetworkSettingsContext } from '../Contexts'
import WorldBasePage from './WorldBasePage'

const ContractsPage: React.FC = () => {
  const networks = useContext(NetworkSettingsContext)
  const { chainId } = useContext(EthersContext)

  const { currency, geotokens, minter, geotokens_v1, minter_v1, geotokens_v2, minter_v2 } = networks?.get(chainId ?? 1)?.addresses ?? {}

  const Currency: React.FC = () => {
    return (
      <Box component={Paper} display="flex" flexDirection="column" paddingX={3}>
        <Typography variant="h4">XYO Erc20 Token</Typography>
        <Typography variant="body1">The XYO Erc20 Token that is used in the XYO Network and is used for minting XYOW GeoTokens.</Typography>
        <Box alignItems="center" display="flex" flexDirection="row" flexWrap="wrap" marginY={1} padding={1}>
          <Typography component={Box} paddingX={1} variant="button">
            1.0
          </Typography>
          <Typography borderLeft={1} borderRight={1} component={Box} paddingX={1} variant="button">
            January, 2018
          </Typography>
          <EthAccountButton variant="outlined" address={currency} full icon={false} />
        </Box>
      </Box>
    )
  }

  const Geotokens: React.FC = () => {
    return (
      <Box component={Paper} display="flex" flexDirection="column" paddingX={3}>
        <Typography variant="h4">XYOW Erc721 Token</Typography>
        <Typography variant="body1">
          The XYOW Erc721 Token is the basis of XYO World. Every Geotoken represents a geographic portion of the world, called a quadtile, defined by
          its id, which is a Quadkey.
        </Typography>
        <Box alignItems="center" display="flex" flexDirection="row" flexWrap="wrap" marginY={1} padding={1}>
          <Typography component={Box} paddingX={1} variant="button">
            3.0
          </Typography>
          <Typography borderLeft={1} borderRight={1} component={Box} paddingX={1} variant="button">
            September 15, 2020
          </Typography>
          <EthAccountButton variant="outlined" address={geotokens} full icon={false} />
          <Typography borderLeft={1} component={Box} paddingX={1} variant="button">
            <Link component={RouterLink} to="/fork3">
              Update Notes
            </Link>
          </Typography>
        </Box>
        <Box alignItems="center" display="flex" flexDirection="row" flexWrap="wrap" marginY={1} padding={1}>
          <Typography component={Box} paddingX={1} variant="button">
            2.0
          </Typography>
          <Typography borderLeft={1} borderRight={1} component={Box} paddingX={1} variant="button">
            September 2, 2020
          </Typography>
          <EthAccountButton variant="outlined" address={geotokens_v2} full icon={false} />
          <Typography borderLeft={1} component={Box} paddingX={1} variant="button">
            Use for historical events only
          </Typography>
          <Typography borderLeft={1} component={Box} paddingX={1} variant="button">
            <Link component={RouterLink} to="/fork/2">
              Update Notes
            </Link>
          </Typography>
        </Box>
        <Box alignItems="center" display="flex" flexDirection="row" flexWrap="wrap" marginY={1} padding={1}>
          <Typography component={Box} paddingX={1} variant="button">
            1.0
          </Typography>
          <Typography borderLeft={1} borderRight={1} component={Box} paddingX={1} variant="button">
            August 25, 2020
          </Typography>
          <EthAccountButton variant="outlined" address={geotokens_v1} full icon={false} />
          <Typography borderLeft={1} component={Box} paddingX={1} variant="button">
            Use for historical events only
          </Typography>
        </Box>
      </Box>
    )
  }

  const Minter: React.FC = () => {
    return (
      <Box component={Paper} display="flex" flexDirection="column" paddingX={3}>
        <Typography variant="h4">XYOW Minter</Typography>
        <Typography variant="body1">
          The XYOW Minter contract is how XYOW tokens are generated and distributed. It conforms to a set of rules that allow users to use XYO Erc20
          Tokens to mint XYOW Geotokens.
        </Typography>
        <Box alignItems="center" display="flex" flexDirection="row" flexWrap="wrap" marginY={1} padding={1}>
          <Typography component={Box} paddingX={1} variant="button">
            3.0
          </Typography>
          <Typography borderLeft={1} borderRight={1} component={Box} paddingX={1} variant="button">
            September 15, 2020
          </Typography>
          <EthAccountButton variant="outlined" address={minter} full icon={false} />
          <Typography borderLeft={1} component={Box} paddingX={1} variant="button">
            <Link component={RouterLink} to="/fork3">
              Update Notes
            </Link>
          </Typography>
        </Box>
        <Box alignItems="center" display="flex" flexDirection="row" flexWrap="wrap" marginY={1} padding={1}>
          <Typography component={Box} paddingX={1} variant="button">
            2.0
          </Typography>
          <Typography borderLeft={1} borderRight={1} component={Box} paddingX={1} variant="button">
            September 2, 2020
          </Typography>
          <EthAccountButton variant="outlined" address={minter_v2} full icon={false} />
          <Typography borderLeft={1} component={Box} paddingX={1} variant="button">
            Use for historical events only
          </Typography>
          <Typography borderLeft={1} component={Box} paddingX={1} variant="button">
            <Link component={RouterLink} to="/fork/2">
              Update Notes
            </Link>
          </Typography>
        </Box>
        <Box alignItems="center" display="flex" flexDirection="row" flexWrap="wrap" marginY={1} padding={1}>
          <Typography component={Box} paddingX={1} variant="button">
            1.0
          </Typography>
          <Typography borderLeft={1} borderRight={1} component={Box} paddingX={1} variant="button">
            August 25, 2020
          </Typography>
          <EthAccountButton variant="outlined" address={minter_v1} full icon={false} />
          <Typography borderLeft={1} component={Box} paddingX={1} variant="button">
            Use for historical events only
          </Typography>
        </Box>
      </Box>
    )
  }

  return (
    <WorldBasePage>
      <Helmet title="Contract Summary" />
      <Container>
        <Box display="flex" flexDirection="column">
          <Box display="flex" flexDirection="column" padding={1}>
            <Typography variant="h1">XYO World Contracts</Typography>
            <Typography variant="body1">
              XYO World consists of three Smart Contracts that run on the Ethereum network. Once these contracts were published and started, there is
              no way to turn them off or change them in any way.
            </Typography>
          </Box>
          <Box padding={1}>
            <Currency />
          </Box>
          <Box padding={1}>
            <Geotokens />
          </Box>
          <Box padding={1}>
            <Minter />
          </Box>
        </Box>
      </Container>
    </WorldBasePage>
  )
}

export default ContractsPage
